import { useEffect, useState } from 'react';

import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  type TimezoneOption,
  filterTimezoneOptions,
  getBrowserTimezone,
  getTimezoneOptions,
  resolveTimezoneOption,
} from '../utils/timezoneData';

interface DailyScheduleConfigProps {
  value: {
    hour: number;
    minute: number;
    timeZone: string;
  };
  onChange: (value: { hour: number; minute: number; timeZone: string }) => void;
  error?: string;
  disabled?: boolean;
}

const HOURS_12 = Array.from({ length: 12 }, (_, i) => (i === 0 ? 12 : i));
// Match legacy: full minute granularity (0–59)
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

export const DailyScheduleConfig = ({
  value,
  onChange,
  error,
  disabled = false,
}: DailyScheduleConfigProps) => {
  const [timezoneOptions] = useState<TimezoneOption[]>(() => getTimezoneOptions());

  // Convert 24h to 12h format
  const get12Hour = () => {
    const h = value.hour % 12;
    return h === 0 ? 12 : h;
  };

  const getAmPm = () => (value.hour >= 12 ? 'PM' : 'AM');

  const handleHourChange = (event: SelectChangeEvent<number>) => {
    const hour12 = event.target.value as number;
    const isPM = value.hour >= 12;

    let hour24: number;
    if (hour12 === 12) {
      hour24 = isPM ? 12 : 0;
    } else {
      hour24 = isPM ? hour12 + 12 : hour12;
    }

    onChange({ ...value, hour: hour24 });
  };

  const handleMinuteChange = (event: SelectChangeEvent<number>) => {
    onChange({ ...value, minute: event.target.value as number });
  };

  const handleAmPmChange = (event: SelectChangeEvent<string>) => {
    const isPM = event.target.value === 'PM';
    let hour24 = value.hour % 12;
    if (isPM) hour24 += 12;
    onChange({ ...value, hour: hour24 });
  };

  const handleTimezoneChange = (_event: React.SyntheticEvent, newValue: TimezoneOption | null) => {
    if (newValue && newValue.value) {
      onChange({ ...value, timeZone: newValue.value });
    }
  };

  // Initialize timezone to browser default if not set
  useEffect(() => {
    if (!value.timeZone) {
      onChange({ ...value, timeZone: getBrowserTimezone() });
    }
  }, [onChange, value]);

  const selectedTimezone = resolveTimezoneOption(value.timeZone, timezoneOptions);

  return (
    <Stack spacing={3}>
      {/* Time Selection */}
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Run at
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 70 }} disabled={disabled}>
            <InputLabel>Hour</InputLabel>
            <Select value={get12Hour()} onChange={handleHourChange} label="Hour">
              {HOURS_12.map((h) => (
                <MenuItem key={h} value={h}>
                  {h.toString().padStart(2, '0')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="h6">:</Typography>

          <FormControl size="small" sx={{ minWidth: 70 }} disabled={disabled}>
            <InputLabel>Min</InputLabel>
            <Select value={value.minute} onChange={handleMinuteChange} label="Min">
              {MINUTES.map((m) => (
                <MenuItem key={m} value={m}>
                  {m.toString().padStart(2, '0')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 70 }} disabled={disabled}>
            <Select value={getAmPm()} onChange={handleAmPmChange}>
              <MenuItem value="AM">AM</MenuItem>
              <MenuItem value="PM">PM</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Timezone Selection */}
      <Box>
        <Autocomplete
          value={selectedTimezone}
          onChange={handleTimezoneChange}
          options={timezoneOptions.filter((tz) => tz.value !== '')}
          filterOptions={filterTimezoneOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          getOptionDisabled={(option) => option.value === ''}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Timezone"
              placeholder="Select timezone"
              error={!!error}
              helperText={error}
            />
          )}
          disabled={disabled}
          size="small"
        />
      </Box>

      {/* Schedule Preview */}
      <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          This schedule will run every day at{' '}
          <strong>
            {get12Hour()}:{value.minute.toString().padStart(2, '0')} {getAmPm()}
          </strong>{' '}
          in <strong>{value.timeZone || 'your local timezone'}</strong>.
        </Typography>
      </Box>
    </Stack>
  );
};
